<template>
  <AdminLayout :overflow="true">
    <Head :title="count.name" />

    <PanelGroup class="flex-1 flex overflow-auto">
      <div class="w-full overflow-auto flex flex-col flex-1">
        <div
          id="count-header"
          class="bg-gray-300 p-4 xl:px-12 top-0 z-10 sticky"
        >
          <div class="flex justify-between">
            <div>
              <h1 class="text-blue-600 font-semibold text-2xl">
                {{ count.name }}
              </h1>

              <div class="flex text-gray-600 items-center my-2 text-sm gap-3">
                <span class="flex items-center gap-1">
                  <CalendarIcon
                    class="h-4 w-4 text-gray-600"
                    aria-hidden="true"
                  />
                  {{ _date(count.initiated_at) }}
                </span>
                <span class="flex items-center gap-1">
                  <ClockIcon
                    class="h-4 w-4 text-gray-600"
                    aria-hidden="true"
                  />
                  {{ _time(count.initiated_at) }}
                </span>
                <span class="flex items-center gap-1">
                  <LocationMarkerIcon
                    class="h-4 w-4 text-gray-600"
                    aria-hidden="true"
                  />
                  {{ count.location_name }}
                  <span v-if="count.spaces?.length">
                    -
                    {{
                      count.spaces
                        ?.map((space) => space.name)
                        .sort()
                        .join(', ')
                    }}</span
                  >
                </span>
                <span class="flex items-center gap-1">
                  <TagIcon
                    class="h-4 w-4 text-gray-600"
                    aria-hidden="true"
                  />
                  {{ count.display_type }}
                </span>
              </div>
            </div>
            <div
              v-if="isComplete"
              class="flex"
            >
              <!-- EXPORT BUTTON -->
              <div class="flex items-center">
                <button
                  @click="exportCount()"
                  type="button"
                  class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
                >
                  Export
                </button>
              </div>
            </div>
          </div>

          <div
            v-if="!isComplete"
            class="input-group relative flex flex-col-reverse gap-y-5 justify-end items-end mt-5"
          >
            <div
              v-if="countError"
              class="flex w-full bg-red-600 rounded px-3 py-3 text-gray-100 justify-between text-lg"
            >
              <p>{{ countError }}</p>
              <XCircleIcon
                @click="countError = null"
                class="h-6 w-6 cursor-pointer"
                aria-hidden="true"
              />
            </div>

            <div class="relative flex gap-3 w-full">
              <div
                v-if="selectedCount"
                class="relative flex flex-initial items-stretch grow focus-within:z-10 w-100"
              >
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon
                    class="h-4 w-4 text-blue-600"
                    aria-hidden="true"
                  />
                </div>
                <input
                  :value="selectedCount.item.package.details.name"
                  disabled
                  type="text"
                  class="pr-9 pl-9"
                />
                <div
                  @click="clearCountItem()"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <MinusCircleIcon
                    class="h-5 w-5 text-gray-600"
                    aria-hidden="true"
                  />
                </div>
              </div>
              <div
                v-else
                class="relative flex flex-1 items-stretch grow w-100 focus-within:z-10"
              >
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon
                    class="h-4 w-4 text-blue-600"
                    aria-hidden="true"
                  />
                </div>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search Products"
                  class="pl-9"
                />
              </div>
              <!-- Search results -->
              <div
                v-if="searching || (searchResults && searchResults.length > 0)"
                class="p-5 top-10 absolute bg-gray-100 rounded-b-sm w-full shadow z-10"
              >
                <div class="flex-1 relative">
                  <ul
                    ref="results"
                    class="space-y-1"
                  >
                    <div class="search-table-container">
                      <table class="text-sm w-full">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Batch</th>
                            <th>Sub-Lot</th>
                            <th>Space</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-if="searching"
                            tabindex="0"
                          >
                            <td colspan="5"><b>Searching...</b></td>
                          </tr>
                          <tr
                            @click="selectCountItem({ item: item, successAnimation: () => {} })"
                            v-if="!searching"
                            v-for="(item, index) in searchResults"
                            class="cursor-pointer text-blue-600"
                            tabindex="0"
                          >
                            <td>
                              {{ item.package.details.product_name }}
                            </td>
                            <td>
                              {{ item.package.details.name }}
                            </td>
                            <td>
                              {{ item.package.sub_lot_num || '' }}
                            </td>
                            <td>
                              {{ item.package.space?.name || item.space?.name || '' }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </ul>
                </div>
              </div>
              <!-- END Search results -->

              <div>
                <input
                  v-model="countForm.amount"
                  @focus="resetInput"
                  type="number"
                  min="0"
                  placeholder="Qty"
                  class="w-32"
                  ref="number_count"
                />
              </div>

              <button
                @click="storeCountItemCountQuantity"
                type="button"
                :disabled="!selectedCount"
                :class="{
                  'bg-blue-600': saveCountState === SAVE_COUNT_STATE.IDLE || saveCountState === SAVE_COUNT_STATE.SAVING,
                  'bg-green-400': saveCountState === SAVE_COUNT_STATE.SAVED,
                  'bg-red-600': saveCountState === SAVE_COUNT_STATE.ERROR
                }"
                class="w-40 font-bold justify-center inline-flex items-center px-6 py-2 border border-transparent text-sm rounded-md shadow-sm text-gray-100 transition-colors duration-200"
              >
                <transition
                  name="fade"
                  mode="out-in"
                >
                  <span v-if="saveCountState === SAVE_COUNT_STATE.SAVING"><spinner /></span>
                  <CheckCircleIcon
                    v-else-if="saveCountState === SAVE_COUNT_STATE.SAVED"
                    class="w-6 h-6"
                  />
                  <ExclamationCircleIcon
                    v-else-if="saveCountState === SAVE_COUNT_STATE.ERROR"
                    class="w-6 h-6"
                  />
                  <span v-else>Count</span>
                </transition>
              </button>
            </div>

            <!-- FINAL BUTTON -->
            <div
              v-if="canComplete"
              class="flex items-center gap-3"
            >
              <a
                @click.prevent="setCanComplete(false)"
                href="#"
                class="text-gray-600 text-sm"
                >Cancel</a
              >
              <button
                @click="completeCount()"
                :disabled="processing"
                type="button"
                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-red-600 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
              >
                Final
              </button>
            </div>

            <!-- REVIEW BUTTON -->
            <div
              v-if="!canComplete"
              class="flex items-center gap-3"
            >
              <button
                @click="toggleCountsPanel(true)"
                class="inline-flex items-center btn btn-secondary-outline py-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
              >
                Last Counted
              </button>

              <button
                @click="checkCount()"
                :disabled="checking"
                type="button"
                :class="{ 'btn-disable': checking }"
                class="inline-flex items-center btn btn-secondary-outline py-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
              >
                {{ checking ? 'Review...' : 'Review' }}
              </button>

              <button
                @click="exportCount()"
                type="button"
                class="inline-flex items-center btn btn-secondary-outline py-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
              >
                Export
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="hasCheckErrors"
          id="count-alert"
          class="relative flex bg-red-600 left-0 right-0 w-full p-3 items-center"
        >
          <ExclamationCircleIcon
            class="h-7 w-7 mr-5 text-gray-100"
            aria-hidden="true"
          />
          <span class="text-gray-100 leading-snug block text-xs font-semibold">
            WARNING! The items highlighted below don’t match expected. Please recount before submitting for a final
            time.
          </span>
        </div>

        <div
          id="count-product"
          class="p-4 md:py-3 xl:px-12"
        >
          <data-table
            :items="mergedItems"
            :headers="headers"
            :actions="[]"
            :selectable="false"
          >
            <template v-slot:rows="slotRow">
              <count-row
                v-slot:rows="slotRow"
                v-bind="slotRow"
                @selected="selectCountItem"
                :has-error="hasCheckCountError(slotRow.item)"
                :show-expected-amount="showExpectedAmount"
                :count-method="count.method"
              />
              <adjustments-row
                v-slot:rows="slotRow"
                :key="slotRow.item.id"
                v-bind="slotRow"
                v-if="hasAdjustments(slotRow.item) || hasCheckCountError(slotRow.item)"
                :can-edit="!isComplete"
                :adjustment-reasons="adjustmentReasons"
                @adjust="storeCountItemAdjustment"
                @delete-adjustment="deleteCountItemAdjustment"
              />
            </template>
          </data-table>
        </div>
      </div>

      <template #right>
        <FloatPanel
          :open="countsPanelIsOpen"
          :widths="countsPanelWidths"
          location="right"
          class="bg-gray-100 rounded-tl-2xl shadow-xl border border-gray-400 overflow-y-scroll"
          @click-outside="toggleCountsPanel(false)"
          @esc-pressed="toggleCountsPanel(false)"
        >
          <div class="list bg-gray-100 w-full flex-col">
            <div class="w-full flex gap-x-2 items-center justify-between px-5 py-6 sticky top-0 bg-gray-100 shadow">
              <h2 class="font-bold text-blue-600">Last Counted</h2>
              <button @click="toggleCountsPanel(false)">
                <XCircleIcon class="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div
              id="count-items"
              class="w-full divide-y divide-gray-400 mt-6"
            >
              <!-- ITEM -->
              <div
                v-for="countEntry in countEntries"
                class="item flex justify-between items-center py-3 px-5"
              >
                <div class="flex items-center">
                  <span class="count text-gray-600 w-6">
                    {{ countEntry.amount }}
                  </span>
                  <div class="flex flex-col leading-tight px-5">
                    <span class="text-blue-600 font-semibold text-sm">
                      {{ getCountItem(countEntry.parent_id).package.details.product_name || '-' }}
                    </span>
                    <small class="text-gray-600">
                      {{ getCountItem(countEntry.parent_id).package.details.name }}
                      <br />
                      <strong>Sub-Lot: </strong>{{ getCountItem(countEntry.parent_id).package.sub_lot_num || '-' }}
                      <br />
                      <strong>Space: </strong
                      >{{
                        getCountItem(countEntry.parent_id).package.space?.name ||
                        getCountItem(countEntry.parent_id).space?.name ||
                        '-'
                      }}
                    </small>
                  </div>
                </div>
                <a
                  v-if="!isComplete"
                  @click="openRemove(countEntry)"
                  class="text-gray-600 cursor-pointer"
                >
                  <MinusCircleIcon
                    class="h-5 w-5 text-gray-600"
                    aria-hidden="true"
                  />
                </a>
              </div>
              <!-- ITEM -->
            </div>
          </div>
        </FloatPanel>
      </template>
    </PanelGroup>
  </AdminLayout>
</template>

<script setup>
import { ref } from 'vue'
import { XCircleIcon, CheckCircleIcon } from '@heroicons/vue/outline'
import Spinner from '@/Components/Icons/Spinner.vue'

const countsPanelIsOpen = ref(false)
const toggleCountsPanel = (open) => (countsPanelIsOpen.value = open)

const countsPanelWidths = [
  ['(max-width: 640px)', '90%'],
  ['(min-width: 640px) and (max-width: 1440px)', '60%'],
  ['(min-width: 1440px)', '35%']
]
</script>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { router, Head, Link } from '@inertiajs/vue3'
import DataTable from '@/Components/Table/CountDataTable'
import CountRow from '@/Pages/Counts/Components/CountRow'
import AdjustmentsRow from '@/Pages/Counts/Components/AdjustmentsRow'
import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
  SearchIcon,
  MinusCircleIcon,
  ExclamationCircleIcon,
  TagIcon
} from '@heroicons/vue/outline'
import _date from '@/Filters/FriendlyDate'
import _time from '@/Filters/Time'
import _dollars from '@/Filters/Dollars'
import getsErrors from '@/Mixins/getsErrors'
import { FloatPanel, PanelGroup } from '@/Components/Panels'
import { settle } from '@/utils/promises'
import { useConfirmDialog } from '@/Composables/confirm-dialog'

const SAVE_COUNT_STATE = {
  IDLE: 0,
  SAVING: 1,
  SAVED: 2,
  ERROR: 3
}

export default {
  components: {
    FloatPanel,
    PanelGroup,
    AdminLayout,
    Head,
    Link,
    CalendarIcon,
    ClockIcon,
    LocationMarkerIcon,
    SearchIcon,
    MinusCircleIcon,
    TagIcon,
    DataTable,
    CountRow,
    AdjustmentsRow,
    ExclamationCircleIcon
  },

  mixins: [getsErrors],

  props: {
    count: {
      type: Object,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    countEntries: {
      type: Array,
      required: true
    },
    adjustmentReasons: {
      type: Array,
      required: true
    },
    canViewExpected: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      searchQuery: null,
      searching: false,
      searchResults: false,
      selectedCount: null,
      countForm: this.$inertia.form({
        type: 'count',
        amount: null,
        reason: 'Count'
      }),
      deletingEntry: null,
      showingDeleteCountConfirmation: false,
      deleteCountForm: this.$inertia.form({}),
      checkForm: this.$inertia.form({}),
      checking: false,
      checked: false,
      canComplete: false,
      completeForm: this.$inertia.form({}),
      countError: null,
      /**
       * An array of updated item objects. Used to override items in this.items prop.
       */
      updatedItems: [],

      SAVE_COUNT_STATE,
      saveCountState: SAVE_COUNT_STATE.IDLE,
      processing: false
    }
  },

  watch: {
    searchQuery(newVal, oldVal) {
      if (!newVal) {
        this.clearSearch()
      } else {
        this.search()
      }
    },
    items: {
      deep: true,
      handler() {
        // Reset updated items as items have been refreshed from server.
        this.updatedItems = []
      }
    }
  },

  computed: {
    headers() {
      return ['Product', 'Count', 'Expected']
    },
    checkErrors() {
      return this.checkForm.errors || {}
    },
    hasCheckErrors() {
      return !_.isEmpty(this.checkErrors)
    },
    completeErrors() {
      return this.completeForm.errors || {}
    },
    hasCompleteErrors() {
      return !_.isEmpty(this.completeErrors)
    },
    isComplete() {
      return this.count.completed_at !== null
    },
    /**
     * This should be moved to be handled by the controller resource and auth check.
     * @returns {boolean|Boolean}
     */
    showExpectedAmount() {
      return this.isComplete || this.canViewExpected
    },
    /**
     * Merges items with updatedItems.
     * @returns {array}
     */
    mergedItems() {
      return this.items.map((item) => {
        let updatedItem = _.find(this.updatedItems, function (updatedItem) {
          return updatedItem.id === item.id
        })

        return updatedItem || item
      })
    }
  },

  methods: {
    _date,
    _time,
    _dollars,
    getCountItem(id) {
      return _.find(this.mergedItems, function (item) {
        return item.id === id
      })
    },

    resetInput() {
      this.$refs['number_count'].value = ''
    },

    /**
     * Adds or re-pushes an item in the this.updatedItems array.
     * @param item
     */
    addUpdatedItem(item) {
      console.log('Adding updated item', item)

      this.updatedItems = this.updatedItems.filter((existing) => {
        return existing.id !== item.id
      })

      this.updatedItems.push(item)
    },

    /**
     * Search methods
     */
    search: _.debounce(
      function () {
        this.searching = true

        axios.post(route('counts.search', { count: this.count.id }), { query: this.searchQuery }).then((response) => {
          this.searchResults = response.data.data || []
          this.searching = false

          if (!this.searchQuery) {
            this.clearSearch()
          }
        })
      },
      500,
      {
        leading: false,
        trailing: true
      }
    ),
    clearSearch() {
      this.searchQuery = null
      this.searchResults = []
      this.searching = false
    },
    /**
     * Item count methods
     */
    selectCountItem(selection) {
      this.selectedCount = selection
      this.countForm.amount = null
      this.clearSearch()
    },
    clearCountItem() {
      this.selectedCount = null
    },
    storeCountItemCountQuantity() {
      this.storeCountItemBalanceEntry(this.selectedCount.item.id, this.countForm)
    },
    storeCountItemAdjustment(id, entry) {
      console.log('storing count item adjustment', id, entry)
      this.storeCountItemBalanceEntry(id, entry)
    },
    storeCountItemBalanceEntry(id, entry) {
      this.countError = null
      this.saveCountState = this.SAVE_COUNT_STATE.SAVING
      axios
        .post(route('countItems.balanceEntries.store', { countItem: id }), entry)
        .then((response) => {
          router.reload({ only: ['countEntries'] })

          let item = response.data.data || []
          this.addUpdatedItem(item)

          this.saveCountState = this.SAVE_COUNT_STATE.SAVED
          this.selectedCount.successAnimation()
        })
        .catch((error) => {
          this.saveCountState = this.SAVE_COUNT_STATE.ERROR
          if (error.response.status === 422) {
            this.countError = error.response.data.message
          }
        })
        .finally(() => {
          setTimeout(() => {
            this.saveCountState = this.SAVE_COUNT_STATE.IDLE
          }, 1200)
        })
    },
    async openRemove(entry) {
      this.deletingEntry = entry
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          type: 'danger',
          title: 'Removing Item from Inventory Count',
          body:
            'Are you sure you want to remove ' +
            this.deletingEntry.amount +
            ' quantity of ' +
            this.getCountItem(this.deletingEntry.parent_id).package.details.product_name +
            ' from inventory count?'
        })
      )

      if (!error) {
        this.submitRemoveEntry(this.deletingEntry)
      }
    },
    submitRemoveEntry(entry) {
      this.deleteCountItemBalanceEntry(entry.parent_id, entry.id)
    },
    deleteCountItemAdjustment(id, balanceEntryId) {
      console.log('deleting count item adjustment', id, balanceEntryId)
      this.deleteCountItemBalanceEntry(id, balanceEntryId)
    },
    deleteCountItemBalanceEntry(countItemId, balanceEntryId) {
      axios
        .delete(route('countItems.balanceEntries.destroy', { countItem: countItemId, balanceEntry: balanceEntryId }))
        .then((response) => {
          router.reload({ only: ['countEntries'] })
          let item = response.data.data || []
          this.addUpdatedItem(item)
        })
    },
    /**
     * Adjustments methods
     */
    hasAdjustments(item) {
      return !_.isEmpty(item.adjustments)
    },
    /**
     * Check and complete functions
     */
    resetCheckAndComplete() {
      this.setCanComplete(false)
      this.checkForm.reset()
      this.completeForm.reset()
    },
    checkCount() {
      this.checking = true

      axios
        .post(route('counts.check', { count: this.count.id }), this.checkForm)
        .then((response) => {
          console.log('count check response', response)

          this.checkForm.clearErrors()
          this.setCanComplete(true)
        })
        .catch((error) => {
          console.log('count check error', error.response.data)

          // Set checkForm errors from error response.
          // Used to highlight count items that need adjusting.
          this.checkForm.clearErrors()
          this.checkForm.errors = error.response.data.errors
          this.checkForm.hasErrors = true

          this.setCanComplete(false)
        })
        .finally(() => {
          this.checking = false
          this.checked = true
        })

      // this.checkForm.post(route('counts.check', { count: this.count.id }), {
      //     errorBag: 'checkCount',
      //     onSuccess: (response) => {
      //         console.log("checkCount()", "onSuccess()", response);
      //         this.setCanComplete(true);
      //     },
      //     onError: (response) => {
      //         console.log("checkCount()", "onError()", response);
      //         this.setCanComplete(false);
      //     },
      //     onFinish: (response) => {
      //         console.log("checkCount()", "onFinish()", response);
      //         this.checked = true;
      //     },
      // });
    },
    hasCheckCountError(item) {
      return !!this.getErrors(`item_${item.id}_count`, this.checkForm)
    },
    setCanComplete(to) {
      this.canComplete = !!to
    },
    completeCount() {
      if (this.processing) {
        return
      }

      this.processing = true
      this.completeForm.post(route('counts.complete', { count: this.count.id }), {
        errorBag: 'completeCount',
        onSuccess: (response) => {
          //
        },
        onFinish: () => {
          this.processing = false
        }
      })
    },
    exportCount() {
      window.open(route('counts.export', { count: this.count.id }), '_blank')
    }
  }
}
</script>

<style scoped>
.select-table-container table thead tr th:nth-child(2) {
  width: 70%;
}

.select-table-container table thead tr th:nth-child(3),
.select-table-container table thead tr th:nth-child(4),
.select-table-container table tbody tr td:nth-child(3),
.select-table-container table tbody tr td:nth-child(4) {
  text-align: center;
}
</style>
